// Port of legacy HighChartModels.EmdLine.formatSvgForExport (highchart_models/emd/line.coffee).
// Two composite branches, keyed off which marker group the chart carries:
//   timeslice — normalize the capsule's CSS transforms into SVG attributes
//   pointer   — embed the windmap canvas as a flipped <image> + clone the heatmap
//               mask/group from the sibling overlay SVG into the chart clone
//
// Everything runs on a DETACHED clone. Transform/width normalization therefore
// reads the elements' INLINE styles (style.transform / style.width) — the exact
// values renderTimeslice/updateTimeslice wrote. This matches legacy semantics:
// jQuery .css() falls back to the inline style on detached nodes, while a naive
// getComputedStyle() port would return empty strings and drop every transform.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/09-emd.md §3.3

const SVG_NS = 'http://www.w3.org/2000/svg'
const XLINK_NS = 'http://www.w3.org/1999/xlink'

// TS lib target predates String#replaceAll — global-regex replace is equivalent.
const stripPx = (value: string) => value.replace(/px/g, '')

// SVG `transform` attributes are unit-less — translate(10px, 20px) is invalid.
const normalizeInlineTransform = (el: Element) => {
  const transform = stripPx((el as SVGElement).style?.transform || '')
  if (transform && transform !== 'none') el.setAttribute('transform', transform)
  el.removeAttribute('style')
}

type Args = {
  chart: any // Highcharts.Chart — captured via the builder's onMount
  container: HTMLElement // the chart wrapper — holds the heatmap SVG + windmap canvas siblings
}

export function buildEmdCompositeForExport({ chart, container }: Args): string {
  const source = chart?.container?.firstChild as SVGSVGElement | null
  if (!source) throw new Error('emd: Highcharts SVG not found')

  const svg = source.cloneNode(true) as SVGSVGElement

  // Standard HC cleanup (matches getItemChart / every other formatSvgForExport).
  svg.querySelectorAll('g.highcharts-button:not(.highcharts-breadcrumbs-button)').forEach(el => el.remove())
  const bg = svg.querySelector('rect.highcharts-background')
  bg?.setAttribute('fill', '#ffffff')

  const timesliceEl = svg.querySelector('g.highcharts-timeslice')
  const pointerEl = svg.querySelector('g.highcharts-pointer')

  if (timesliceEl) {
    normalizeInlineTransform(timesliceEl)

    timesliceEl.querySelectorAll('.highcharts-timeslice-ticks').forEach(tick => {
      const width = stripPx((tick as SVGElement).style?.width || '')
      const transform = stripPx((tick as SVGElement).style?.transform || '')
      if (transform && transform !== 'none') tick.setAttribute('transform', transform)
      if (width) tick.setAttribute('width', width)
      tick.removeAttribute('style')
    })

    timesliceEl.querySelectorAll('circle').forEach(normalizeInlineTransform)
  } else if (pointerEl) {
    const chartId = chart.container.id
    const canvas = container.querySelector(`#map-${chartId}`)
    if (!(canvas instanceof HTMLCanvasElement)) throw new Error('emd: windmap canvas not found')

    const { width, height, x: marginX } = chart.plotBox

    // Canvas y-axis is CSS-flipped (scale(1,-1)) on the live overlay — replicate
    // the flip on the embedded image so the flow field keeps its orientation.
    const imageNode = document.createElementNS(SVG_NS, 'image')
    imageNode.setAttributeNS(XLINK_NS, 'xlink:href', canvas.toDataURL())
    imageNode.setAttribute('width', String(width))
    imageNode.setAttribute('height', String(height))
    imageNode.setAttribute('x', String(marginX))
    imageNode.setAttribute('y', '0')
    imageNode.setAttribute('transform', `scale(1, -1) translate(0, -${height})`)

    svg.setAttribute('xmlns:xlink', XLINK_NS)

    // The heatmap overlay is a sibling SVG (#heatmap-{chartId}). Its mask joins the
    // chart's <defs>; the masked group lands right above the background rect. The
    // gradient/filter the group references (batch-gradient-*, gaussianBlur) already
    // live in the chart's own defs (drawDefs writes them there), so they resolve.
    const heatmapSvg = container.querySelector(`#heatmap-${chartId}`)
    const heatmapMask = heatmapSvg?.querySelector('defs mask')
    const heatmapGroup = heatmapSvg?.querySelector('g.heatmap')

    // Legacy insertion order: image after bg, then group after bg → bg, group, image.
    if (bg) bg.insertAdjacentElement('afterend', imageNode)
    if (heatmapMask) svg.querySelector('defs')?.appendChild(heatmapMask.cloneNode(true))
    if (heatmapGroup && bg) bg.insertAdjacentElement('afterend', heatmapGroup.cloneNode(true) as Element)

    svg.querySelectorAll('.highcharts-markers').forEach(el => el.remove())

    normalizeInlineTransform(pointerEl)
  }

  return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svg)
}
